import { 
  Box, 
  Button, 
  CardContent,  
  Stack, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Typography,
  styled,
  useTheme,
  Chip,
  Grid,
  Paper
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { fetchHistoricalSummary,downloadS3File } from "../../services/historicalService";
import { useCallback, useEffect, useState } from "react";
import { historicalStats, historicalSummary } from "../../types/types";
import { Loader } from "../../components/loader/Loader";
import { TranslationEnum, usePortalTranslation } from "../../providers/TranslationProvider";
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#24c6b1',
    color: theme.palette.common.white,
    fontWeight: 600,
    padding: theme.spacing(1.5),
    textAlign: 'center',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: theme.spacing(1.5),
    textAlign: 'center',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    transition: 'background-color 0.3s ease',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StatusBadge = ({ status }: { status: string }) => {
  const theme = useTheme();
  
  return (
    <Chip
      label={status}
      size="small"
      icon={status === "Success" ? <CheckCircleIcon /> : <ErrorIcon />}
      color={status === "Success" ? "success" : "error"}
      sx={{ 
        fontWeight: 600,
        px: 1,
        '& .MuiChip-icon': {
          color: status === "Success" ? theme.palette.success.light : theme.palette.error.light
        }
      }}
    />
  );
};

const StatusBadgeInner = ({ status }: { status: string }) => {
  const theme = useTheme();
  
  return (
    <Chip
      label={status}
      size="small"
      icon={status === "completed" ? <CheckCircleIcon /> : <ErrorIcon />}
      color={status === "completed" ? "success" : "error"}
      sx={{ 
        fontWeight: 600,
        px: 1,
        '& .MuiChip-icon': {
          color: status === "completed" ? theme.palette.success.light : theme.palette.error.light
        }
      }}
    />
  );
};

export function HistoricalData() {
  const { translate } = usePortalTranslation();
  const theme = useTheme();
  const authContext = JSON.parse(localStorage.authContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [jobSummary, setJobSummary] = useState<historicalSummary[]>([]);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true)
    const response: historicalSummary[] = await fetchHistoricalSummary(authContext.env, authContext.user.lob);
   // console.log(JSON.stringify(response))
    setJobSummary(response);
    setIsLoading(false);
  }, [authContext.env, authContext.user.lob])

  const handleBackClick = () => {
    window.history.back();
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(); // Basic formatting
      // OR for more control:
      // return `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}`;
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {isLoading ? (
        <Loader />
      ) : (
        <Stack spacing={4}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {translate(TranslationEnum.historical_cleanup, "Historical DataCleanup")}
              </Typography>
            </Grid>
            <Grid item>
              <Button 
                variant="contained" 
                onClick={handleBackClick}
                sx={{
                  fontWeight: 'bold',
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                  }
                }}
              >
                {translate(TranslationEnum.common_portal, "BACK")}
              </Button>
            </Grid>
          </Grid>

          {jobSummary.length > 0 ? (
            <Stack spacing={3}>
              {jobSummary.map((summary, index) => (
                <Paper 
                  key={`${summary}-${index}`} 
                  elevation={3}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    '&:hover': {
                      boxShadow: theme.shadows[6],
                    }
                  }}
                >
                  <StatBox 
                    stats={summary.groupItems} 
                    //dateString={formatDate(summary.taskDate)} 
                    dateString={summary.taskDate} 
                    group_id={summary.group_Id} 
                    status={summary.status}
                  />
                </Paper>
              ))}
            </Stack>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: theme.palette.grey[50] }}>
              <Typography variant="body1" color="text.secondary">
                {translate(TranslationEnum.historical_cleanup, "No historical cleanup found for the selected environment and lob")}
              </Typography>
            </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
}

function StatBox(props: Readonly<{
  stats: string;
  dateString: string;
  group_id: string;
  status: string;
}>) {
  const { translate } = usePortalTranslation();
  const theme = useTheme();
  const result: historicalStats[] = JSON.parse(props.stats);
  
  return (
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ 
        p: 3, 
        backgroundColor: theme.palette.grey[100],
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={600}>
              {translate(TranslationEnum.historical_cleanup, "Date")}: {props.dateString}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={600}>
              {translate(TranslationEnum.historical_cleanup, "Group Id")}: {props.group_id}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={600}>
              {translate(TranslationEnum.historical_cleanup, "Job Status")}:{' '}
              <StatusBadge status={props.status} />
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            {/* First header row */}
            <TableRow>
              <TableCell
              key='Table Name'
              rowSpan={2}
              
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
              Table Name
            </TableCell>
             
              <TableCell
              key='Record Count'
              colSpan={2}
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
              Record Count
            </TableCell>

            <TableCell
              key='Delete Count'
              colSpan={2}
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
              Delete Count
            </TableCell>

            <TableCell
              key='Not Found'
              rowSpan={2}
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
              Not Found
            </TableCell>
              
            <TableCell
              key='Status'
              rowSpan={2}
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
              Status
            </TableCell>
              
            <TableCell
              key='File Links'
              colSpan={2}
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
              File Links
            </TableCell>
              
            <TableCell
              key='Date Range'
              colSpan={2}
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
              Date Range
            </TableCell>
              
      
            </TableRow>

            {/* Second header row */}
            <TableRow>
            <TableCell
              key='MySql'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             MySql
            </TableCell>

            <TableCell
              key='S3'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             S3
            </TableCell>
            <TableCell
              key='Success'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             Success
            </TableCell>
            <TableCell
              key='Failure'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             Failure
            </TableCell>
            <TableCell
              key='NotFound'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             NotFound
            </TableCell>
            <TableCell
              key='FailToDelete'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             FailToDelete
            </TableCell>
            <TableCell
              key='StartDate'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             StartDate
            </TableCell>
            <TableCell
              key='EndDate'
              sx={{ 
                backgroundColor: '#f0fbff',
                color: '#227c9d',
                fontWeight: 600,
                fontSize: '14px',
                padding: theme.spacing(2),
                textAlign: 'center'
              }}
            >
             EndDate
            </TableCell>
              
              
            </TableRow>
          </TableHead>

          <TableBody>
            {result.map((object, index) => (
              <StyledTableRow key={`${object}-${index}`}>
                <StyledTableCell>{object.table_name}</StyledTableCell>
                <StyledTableCell>{object.totalexternalrecords}</StyledTableCell>
                <StyledTableCell>{object.totalredshiftrecords}</StyledTableCell>
                <StyledTableCell>{object.successdelete}</StyledTableCell>
                <StyledTableCell>{object.faileddelete}</StyledTableCell>
                <StyledTableCell>{object.totalmissing}</StyledTableCell>
                <StyledTableCell>
                  <StatusBadgeInner status={object.status} />
                </StyledTableCell>
                <StyledTableCell>
                  {object.notfoundfilepath ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={object.notfoundfilepath}
                      download
                      onClick={() => downloadS3File(object.notfoundfilepath)}
                      sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.light,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.light,
                        }
                      }}
                    >
                      Download
                    </Button>
                  ) : (
                    'NA'
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  {object.failedtodeletepath ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={object.failedtodeletepath}
                      download
                      onClick={() => downloadS3File(object.failedtodeletepath)}
                      sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.light,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.light,
                        }
                      }}
                    >
                      Download
                    </Button>
                  ) : (
                    'NA'
                  )}
                </StyledTableCell>
                <StyledTableCell>{object.startdate}</StyledTableCell>
                <StyledTableCell>{object.enddate}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </CardContent>
  );
}


export default HistoricalData;